import { useTranslation } from 'react-i18next';
import { LifeBuoy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSupportMessengerContext } from './SupportMessengerProvider';

/**
 * Discreet "Need help?" entry point for a logged-out visitor on an allow-listed public page
 * (e.g. /claim/:token). Renders nothing until `canOfferGuestHelp` says yes (logged out +
 * claim-page family + App ID configured + nothing booted yet — see
 * src/lib/support/gate.ts's shouldOfferGuestSupportButton). No widget code loads and no cookie
 * is set until this is clicked — "on the visitor's explicit request", per the 2026-07-21
 * decision (see the customer-support handover doc's decisions), which is why this needs no
 * consent banner.
 * Disappears the instant the click succeeds — the vendor's own launcher bubble takes over from
 * there, so the two never overlap visually.
 */
export function SupportGuestButton() {
  const { t } = useTranslation();
  const { canOfferGuestHelp, bootGuestSession } = useSupportMessengerContext();

  if (!canOfferGuestHelp) return null;

  return (
    <div className="fixed bottom-4 right-4 z-40">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2 rounded-full bg-background shadow-lg"
        onClick={bootGuestSession}
      >
        <LifeBuoy className="h-4 w-4" aria-hidden="true" />
        {t('support.guest_help')}
      </Button>
    </div>
  );
}
